import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, database } from '../../firebaseConfig.js';
import { doc, getDoc } from 'firebase/firestore';
import {
  FaRegBuilding,
  FaWallet,
  FaBars,
  FaTimes,
  FaUserCircle,
  FaQuestionCircle,
  FaChevronRight,
  FaEdit,
  FaShieldAlt,
  FaHandSparkles,
  FaHome,
  FaCog,
  FaBell,
  FaGraduationCap
} from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';

const Sidebar = ({ isAdmin }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();

  // Initialize isDesktop and isOpen based on window width
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return false;
  });
  const [isOpen, setIsOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return false;
  });
  const [activePath, setActivePath] = useState('/');
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const sidebarRef = useRef(null);
  const [userName, setUserName] = useState('User');
  const [user, setUser] = useState(null);

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  useEffect(() => {
    setActivePath(location.pathname);
  }, [location]);

  // Fetch user data and name
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        try {
          const userRef = doc(database, 'users', currentUser.uid);
          const userDoc = await getDoc(userRef);
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const name = userData?.personalInfo?.name || currentUser.displayName || currentUser.email?.split('@')[0] || 'User';
            setUserName(name);
          } else {
            // Fallback to display name or email
            const name = currentUser.displayName || currentUser.email?.split('@')[0] || 'User';
            setUserName(name);
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
          const name = currentUser.displayName || currentUser.email?.split('@')[0] || 'User';
          setUserName(name);
        }
      } else {
        setUserName('User');
      }
    });
    return () => unsubscribe();
  }, []);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        window.innerWidth < 768 &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !event.target.closest('button[aria-label="Toggle menu"]')
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (isOpen && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const navigateTo = (path) => {
    navigate(path);
    if (window.innerWidth < 768) {
      setIsOpen(false);
    }
  };

  // Touch handlers for swipe gestures
  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe && isOpen) {
      setIsOpen(false);
    } else if (isRightSwipe && !isOpen && window.innerWidth < 768) {
      setIsOpen(true);
    }
  };

  const menuItems = [
    {
      title: 'Dashboard',
      path: '/',
      icon: FaHome,
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'Make Your Own Test',
      path: '/make-your-own-test',
      icon: FaEdit,
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Profile',
      path: '/Profile',
      icon: FaUserCircle,
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Attempted Questions',
      path: '/attempted-questions',
      icon: FaQuestionCircle,
      color: 'from-orange-500 to-red-500'
    },
    {
      title: 'Notifications',
      path: '/notifications',
      icon: FaBell,
      color: 'from-yellow-500 to-amber-500'
    },
    {
      title: 'Settings',
      path: '/plans-billing',
      icon: FaCog,
      color: 'from-gray-500 to-zinc-500'
    },
  ];

  if (isAdmin) {
    menuItems.push({
      title: 'Admin Panel',
      path: '/admin',
      icon: FaShieldAlt,
      color: 'from-red-600 to-rose-600'
    });
  }

  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    },
    closed: {
      x: '-100%',
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    }
  };

  // Handle responsive behavior and keep sidebar open on desktop
  useEffect(() => {
    const checkDesktop = () => {
      const desktop = window.innerWidth >= 768;
      setIsDesktop(desktop);
      // On desktop, always keep sidebar open
      if (desktop) {
        setIsOpen(true);
      } else {
        // On mobile, close sidebar if it was open
        setIsOpen(false);
      }
    };

    // Check on mount and resize
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  const overlayVariants = {
    open: {
      opacity: 1,
      transition: { duration: 0.3 }
    },
    closed: {
      opacity: 0,
      transition: { duration: 0.3 }
    }
  };

  const menuItemVariants = {
    closed: { opacity: 0, x: -20 },
    open: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.05,
        type: 'spring',
        stiffness: 300,
        damping: 25
      }
    })
  };

  // Common sidebar content component
  const SidebarContent = ({ isMobile = false }) => (
    <>
      {/* Logo Section */}
      <div className={`flex items-center px-5 py-5 border-b ${isDark ? 'border-white/5' : 'border-gray-200'
        }`}>
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigateTo('/')}>
          <motion.div
            className="w-10 h-10 bg-gradient-to-br from-purple-600 via-violet-600 to-pink-500 rounded-xl flex items-center justify-center shadow-lg"
            whileHover={{ scale: 1.05, rotate: 5 }}
          >
            <FaGraduationCap className="text-white text-lg" />
          </motion.div>
          <div>
            <h2 className={`text-lg font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'
              }`}>
              PREPIFY<span className="text-purple-500">.LIVE</span>
            </h2>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 sidebar-nav" style={{ scrollbarWidth: 'thin' }}>
        <ul className="space-y-1">
          {menuItems.map((item, index) => {
            const IconComponent = item.icon;
            const isActive = activePath === item.path ||
              (item.path === '/' && activePath === '/') ||
              (item.path !== '/' && activePath.startsWith(item.path));

            const MenuItemContent = (
              <button
                onClick={() => navigateTo(item.path)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group relative ${isActive
                  ? isDark
                    ? 'bg-purple-600/20 text-white'
                    : 'bg-purple-100 text-purple-700'
                  : isDark
                    ? 'text-gray-400 hover:text-white hover:bg-white/5'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
              >
                {/* Active indicator */}
                {isActive && (
                  <motion.div
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-purple-500 to-pink-500 rounded-r-full"
                    layoutId="activeIndicator"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}

                <span className={`p-2 rounded-lg transition-all ${isActive
                  ? `bg-gradient-to-br ${item.color} text-white shadow-lg`
                  : isDark
                    ? 'bg-white/5 group-hover:bg-white/10'
                    : 'bg-gray-100 group-hover:bg-gray-200'
                  }`}>
                  <IconComponent className="text-sm" />
                </span>
                <span className={`font-medium text-sm ${isActive ? 'text-white' : ''
                  }`}>
                  {item.title}
                </span>
                {isActive && (
                  <FaChevronRight className="ml-auto text-xs text-purple-400" />
                )}
              </button>
            );

            return isMobile ? (
              <motion.li
                key={item.path}
                custom={index}
                variants={menuItemVariants}
                initial="closed"
                animate="open"
              >
                {MenuItemContent}
              </motion.li>
            ) : (
              <li key={item.path}>{MenuItemContent}</li>
            );
          })}
        </ul>
      </nav>

      {/* User Profile Section */}
      <div className={`p-4 border-t ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
        <button
          onClick={() => navigateTo('/Profile')}
          className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${isDark
            ? 'bg-white/5 hover:bg-white/10'
            : 'bg-gray-100 hover:bg-gray-200'
            }`}
        >
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
            <span className="text-white font-semibold">
              {userName.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 text-left">
            <p className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'
              }`}>
              {userName}
            </p>
            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              View profile
            </p>
          </div>
          <FaChevronRight className={`text-xs ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Overlay for mobile view */}
      <AnimatePresence>
        {isOpen && !isDesktop && (
          <motion.div
            variants={overlayVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setIsOpen(false)}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          />
        )}
      </AnimatePresence>

      {/* Enhanced Hamburger Menu for Mobile View */}
      <motion.button
        onClick={toggleSidebar}
        className={`md:hidden fixed bottom-6 right-6 z-50 p-4 rounded-full shadow-2xl focus:outline-none transition-all duration-300 ${isDark
          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
          : 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
          }`}
        aria-label="Toggle menu"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        animate={isOpen ? { rotate: 180 } : { rotate: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <FaTimes className="text-xl" />
            </motion.div>
          ) : (
            <motion.div
              key="menu"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <FaBars className="text-xl" />
            </motion.div>
          )}
        </AnimatePresence>
        {/* Pulse animation ring */}
        {!isOpen && (
          <motion.span
            className="absolute inset-0 rounded-full bg-purple-400"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.5, 0, 0.5]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }}
          />
        )}
      </motion.button>

      {/* Sidebar */}
      {isDesktop ? (
        <aside
          ref={sidebarRef}
          className={`h-screen flex flex-col sticky top-0 left-0 w-64 z-auto transition-colors duration-300 ${isDark
            ? 'bg-dark-secondary border-r border-white/5'
            : 'bg-white border-r border-gray-200'
            }`}
          style={{ maxHeight: '100vh', overflowY: 'auto' }}
        >
          <SidebarContent />
        </aside>
      ) : (
        <motion.aside
          ref={sidebarRef}
          variants={sidebarVariants}
          initial={isOpen ? 'open' : 'closed'}
          animate={isOpen ? 'open' : 'closed'}
          className={`h-screen flex flex-col fixed top-0 left-0 w-72 z-50 transition-colors duration-300 ${isDark
            ? 'bg-dark-secondary border-r border-white/5'
            : 'bg-white border-r border-gray-200'
            }`}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <SidebarContent isMobile={true} />
        </motion.aside>
      )}
    </>
  );
};

Sidebar.propTypes = {
  isAdmin: PropTypes.bool
};

Sidebar.defaultProps = {
  isAdmin: false
};

export default Sidebar;